<template>
	<div class="fd-relation-container">
		<div class="fd-relation">
			<div class="fd-relation-icns">
				<object-link v-if="r.src" :src-id="r.src" :src-type="SrcType.PARTY">
					<sym-icon :name="canView ? (r.srcIcon ? r.srcIcon : 'party') : 'forbidden'"></sym-icon>
				</object-link>
				<sym-icon v-else :name="canView ? (r.srcIcon ? r.srcIcon : 'party') : 'forbidden'"></sym-icon>
			</div>
			<div class="fd-relation-party">
				<template v-if="r.src">
					<div class="fd-relation-name-fr">
						<div class="fd-relation-name">
							<object-link :src-id="r.src" :src-type="SrcType.PARTY">{{ partyFileAs }}</object-link>
						</div>
						<more-opener v-model="moreOpen"></more-opener>
					</div>
					<div class="fd-relation-comment">
						<tag-input v-model="r.comment" :disabled="ro" ref="commentInput" :placeholder="env.msg('relation.comment.placeholder')" style="display:inline-block" :min-width="80"></tag-input>
					</div>
				</template>
				<div v-else>
					<suggester-part :data="r" :part="relationSuggesterPart" @suggested="newPartySuggested"></suggester-part>
				</div>
				<show-down v-if="r.src" :show="moreOpen" class="fd-relation-options">
					<div class="fd-relation-name-fr">
						<select v-model="r.type">
							<option v-for="t in relationsList" :value="t.value">{{ t.name }}</option>
						</select>
						<sym-icon v-if="!ro" name="disconnect" class="fd-delete-icon" @click="deleteMember"></sym-icon>
					</div>
				</show-down>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {computed, defineProps, nextTick, ref} from "vue";
import useEditor, {type EditorProps, editorProps} from "../Editor";
import {injectEnv} from "../../Environment";
import ObjectLink from "../../helpers/ObjectLink.vue";
import SymIcon from "../../helpers/SymIcon.vue";
import SuggesterPart from "../suggester/SuggesterPart.vue";
import TagInput from "../suggester/TagInput.vue";
import MoreOpener from "../../helpers/MoreOpener.vue";
import {injectFindList} from "../../PartInjectKeys";
import ShowDown from "../../helpers/ShowDown.vue";
import type {RelationPart} from "@t/fd/editor/RelationPart";
import type {RelationSrcInfo} from "@t/customer/Relation";
import type {Suggester} from "@t/fd/editor/Suggester";
import type {FormList} from "@t/fd/layout/FormDefinition";
import {SrcType} from "@t/meta/SourceType";
import {hasAccess, ObjectAccessFlags} from "@t/security/ObjectAccess.ts";
import {obscureText} from "@/utils/FormatUtil.ts";

const props = defineProps({
	...editorProps
});
const emit = defineEmits(["array-delete"]);
const rl: RelationPart = props.part as RelationPart;

const {value, ro, vm} = useEditor(props as EditorProps);
const env = injectEnv();
const commentInput = ref(null);
const moreOpen = ref<boolean>(false);

const r = computed<RelationSrcInfo>(() => value.value);

const canView = computed<boolean>(() => {
	return hasAccess(r.value, "view");
});

const partyFileAs = computed<string>(() => {
	return canView.value ? r.value.srcName : obscureText(r.value.srcName);
});


const relationSuggesterPart = computed(() => {
	const customized = rl.suggest ? rl.suggest : {};
	let sg: Suggester = {
		type: "suggest",
		property: "src",
		url: "suggestfeed.do",
		suggester: "MemberSuggester",
		minTags: 0,
		maxTags: 1,
		pickerLabel: env.msg('placeholder.party.suggester'),
		propagate: [
			{d: "src", s: "id.main", c: "integer", empty: 0},
			{d: "srcName", s: "title.main", empty: ""},
			{d: "srcIcon", s: "icon", ifmissing: "party"}
		],
		tag: [
			{d: "src", t: "id", empty: 0},
			{d: "src", t: "title", empty: 0, optional: true},
			{d: "srcName", t: "name", empty: ""}
		],
		...customized
	};
	return sg;
});

const findList = injectFindList();

const relationsList = computed<FormList>(() => findList(rl.typeList).value);

function deleteMember() {
	emit("array-delete");
}

function newPartySuggested() {
	nextTick(() => {
		if (commentInput.value) {
			commentInput.value.focus();
			moreOpen.value = true;
		}
	});
}

</script>

<style>
.fd-relation {
	display: flex;
	flex-direction: row;
	line-height: var(--icn-size);
	width: 100%;
	margin-top: var(--form-v-padding);
	margin-bottom: var(--form-v-padding);
	box-sizing: border-box;
}

.fd-relation-icns {
	width: calc(1.5 * var(--icn-size));
	flex-shrink: 0;
	flex-grow: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
}

.fd-relation-party {
	max-width: 400px;
	flex-basis: 100%;
	box-sizing: border-box;
}

.fd-relation-name-fr {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	box-sizing: border-box;
}

.fd-relation-options {
	padding: var(--form-v-padding);
}

</style>
